import { IComponent, ICustomEventData } from '@modules/src/interfaces/IComponents'
import { ContainersNames } from '../../containers/ContainersNames'
import { AssetType } from '@modules/src/enums/AssetType'
import { Orientation } from '@modules/src/enums/Orientation'
import { ComponentEvent } from '@modules/src/enums/ComponentEvent'
import { ModuleSignals } from '@modules/src/signals/ModuleSignals'
import { TranslationUtils } from '@modules/src/utils/TranslationUtils'
import { DeviceUtils } from '@modules/src/utils/DeviceUtils'
import GameSignals from '@/signals/GameSignals'
import { SpineAnimation } from '@base/spineAnimation/SpineAnimation'
import { UNFINISHED_ROUND_TITLE_STYLE } from '@/containers/common/TextStyles'
import { ScalingTextField } from '@/containers/common/ScalingTextField'
import { fadeOutFeaturePopupItem } from '@/gameConfigs/components/FeatureIntroPopupComponent'
import { paintViewportCoverOverlay } from '@dragonframework/modules'
import gsap from 'gsap'
import * as PIXI from 'pixi.js'

const OVERLAY_COLOR = 0x01102a
const OVERLAY_OPACITY = 0.6

const SPINE_SRC = 'popup_message_box'
const ANIM = { in: 'ml_you_won_in', loop: 'ml_you_won_loop', out: 'ml_you_won_out' } as const

const TITLE_FALLBACK = 'YOU HAVE UNFINISHED GAME'

const CONTINUE_STYLE = {
    fontFamily: 'Montserrat_Bold',
    fontSize: 50,
    fill: 0xffffff,
    align: 'center',
}

//## Continue prompt pulse.
const CONTINUE_PULSE_SCALE = 1.1
const CONTINUE_PULSE_DURATION_S = 0.6

//## Box open fallback — if the in clip fails to call its callback, reveal the texts anyway
const BOX_OPEN_FALLBACK_MARGIN_S = 0.35
const BOX_OPEN_FALLBACK_DEFAULT_S = 1

// --- transient state (one popup instance) -------------------------------------------------
let continuePulse: gsap.core.Tween | null = null
let boxOpenFallback: gsap.core.Tween | null = null
let hasContinued = false
let hasBoxOpened = false
let canContinue = false
let isExiting = false
let tapHandler: (() => void) | null = null

const translate = (key: string, fallback: string): string => {
    const translated = TranslationUtils.translate(key)
    return translated === key ? fallback : translated
}

const stopContinuePulse = (element: PIXI.Text | undefined): void => {
    continuePulse?.kill()
    continuePulse = null

    if (element) {
        element.scale.set(1)
    }
}

const startContinuePulse = (element: PIXI.Text): void => {
    stopContinuePulse(element)

    element.scale.set(1)
    continuePulse = gsap.to(element.scale, {
        x: CONTINUE_PULSE_SCALE,
        y: CONTINUE_PULSE_SCALE,
        duration: CONTINUE_PULSE_DURATION_S,
        ease: 'sine.inOut',
        yoyo: true,
        repeat: -1,
    })
}

const clearBoxOpenFallback = (): void => {
    boxOpenFallback?.kill()
    boxOpenFallback = null
}

const openBoxOnce = (): void => {
    if (hasBoxOpened) {
        return
    }

    hasBoxOpened = true
    clearBoxOpenFallback()
    canContinue = true
    ;(GameSignals as any).unfinishedRoundBoxOpened?.emit()
}

const continueOnce = (): void => {
    clearBoxOpenFallback()

    if (hasContinued) {
        return
    }

    hasContinued = true
    ;(GameSignals as any).unfinishedRoundContinue?.emit()
}

const resolveParentContainer = (data: ICustomEventData): any => {
    const animation = data.component.componentElement as SpineAnimation
    return data.component.parentContainer || (animation as any)?.parent || animation
}

const detachTapHandler = (parentContainer: any): void => {
    if (tapHandler && parentContainer) {
        parentContainer.off('pointertap', tapHandler)
    }

    tapHandler = null
}

// --- items --------------------------------------------------------------------------------

type PopupTextItemConfig = {
    name: string
    style: Record<string, unknown>
    position: { orientation: Orientation; x: number; y: number }[]
    resolveText: () => string
    pulse?: boolean
}

const createPopupTextItem = ({ name, style, position, resolveText, pulse = false }: PopupTextItemConfig): any => {
    const maxWidth = Number((style as any).maxWidth)
    const isScaled = Number.isFinite(maxWidth) && maxWidth > 0

    const applyText = (data: ICustomEventData, element: any): void => {
        if (isScaled) {
            element.text = resolveText()
            return
        }

        data.component.setText(resolveText())
        element.anchor.set(0.5)
    }

    return {
        name,
        position,
        ...(isScaled
            ? {
                  type: AssetType.CUSTOM,
                  elementFactory: () => new ScalingTextField(1, maxWidth, PIXI.Text as any, style),
              }
            : {
                  type: AssetType.TEXT,
                  translationKey: '',
                  textStyle: [{ orientation: Orientation.ANY, options: { ...style } }],
              }),
        events: [
            {
                event: ComponentEvent.CUSTOM_NEW,
                onGameSignalsTriggered: [(GameSignals as any).unfinishedRoundRequested],
                callback: function (data: ICustomEventData) {
                    const element = data.component.componentElement as any
                    applyText(data, element)
                    if (pulse) {
                        stopContinuePulse(element)
                    }

                    gsap.killTweensOf(element)
                    element.visible = false
                    element.alpha = 0
                },
            },
            {
                event: ComponentEvent.CUSTOM_NEW,
                onGameSignalsTriggered: [(GameSignals as any).unfinishedRoundBoxOpened],
                callback: function (data: ICustomEventData) {
                    const element = data.component.componentElement as PIXI.Text

                    gsap.killTweensOf(element)
                    element.visible = true
                    element.alpha = 1

                    if (pulse) {
                        startContinuePulse(element)
                    }
                },
            },
            {
                event: ComponentEvent.CUSTOM_NEW,
                onGameSignalsTriggered: [(GameSignals as any).unfinishedRoundDismissStarted],
                callback: function (data: ICustomEventData) {
                    if (pulse) {
                        stopContinuePulse(data.component.componentElement as PIXI.Text)
                    }

                    fadeOutFeaturePopupItem(data)
                },
            },
            ...(pulse
                ? [
                      {
                          event: ComponentEvent.CUSTOM_NEW,
                          onGameSignalsTriggered: [(GameSignals as any).unfinishedRoundContinue],
                          callback: function (data: ICustomEventData) {
                              stopContinuePulse(data.component.componentElement as PIXI.Text)
                          },
                      },
                  ]
                : []),
        ],
    }
}

export const UNFINISHED_ROUND_POPUP_COMPONENT: IComponent = {
    name: ContainersNames.UNFINISHED_ROUND_POPUP_CONTAINER,
    type: AssetType.CONTAINER,
    zIndex: new Map<Orientation, number>([
        [Orientation.LANDSCAPE, 450],
        [Orientation.PORTRAIT, 450],
    ]),
    visible: false,
    events: [
        {
            event: ComponentEvent.SHOW,
            onGameSignalsTriggered: [(GameSignals as any).unfinishedRoundRequested],
        },
        {
            event: ComponentEvent.HIDE,
            onGameSignalsTriggered: [(GameSignals as any).unfinishedRoundContinue],
        },
    ],
    items: [
        {
            name: 'Unfinished Round Overlay',
            type: AssetType.OVERLAY,
            componentStyle: {
                color: OVERLAY_COLOR,
                opacity: OVERLAY_OPACITY,
            },
            events: [
                {
                    event: ComponentEvent.CUSTOM_NEW,
                    onGameSignalsTriggered: [
                        (GameSignals as any).unfinishedRoundRequested,
                        ModuleSignals.gameResized,
                    ],
                    callback: function (data: ICustomEventData) {
                        paintViewportCoverOverlay(
                            data.component.componentElement as PIXI.Graphics,
                            OVERLAY_COLOR,
                            OVERLAY_OPACITY
                        )
                    },
                },
            ],
        },
        {
            name: 'Unfinished Round Spine',
            type: AssetType.SPINE,
            visible: false,
            src: SPINE_SRC,
            animationSettings: {
                default: {
                    triggerGameSignals: [],
                    animNames: [ANIM.in as string],
                    loops: [false],
                    track: 0,
                    timeScales: [1],
                },
            },
            events: [
                {
                    event: ComponentEvent.CUSTOM_NEW,
                    onGameSignalsTriggered: [(GameSignals as any).unfinishedRoundRequested],
                    callback: function (data: ICustomEventData) {
                        hasContinued = false
                        hasBoxOpened = false
                        canContinue = false
                        isExiting = false
                        clearBoxOpenFallback()

                        const animation: SpineAnimation = data.component.componentElement
                        const parentContainer = resolveParentContainer(data)

                        if (!parentContainer) {
                            continueOnce()
                            return
                        }

                        detachTapHandler(parentContainer)

                        parentContainer.interactive = true
                        parentContainer.eventMode = 'static'
                        parentContainer.cursor = 'pointer'

                        tapHandler = () => {
                            if (!canContinue || isExiting) {
                                return
                            }

                            isExiting = true
                            clearBoxOpenFallback()

                            ;(GameSignals as any).unfinishedRoundDismissStarted?.emit()
                            animation.start([ANIM.out], [false], 0, [1], continueOnce)
                        }

                        parentContainer.on('pointertap', tapHandler)

                        try {
                            animation.animation?.state?.clearTracks()
                            animation.stop()
                        } catch (error) {
                            // ignore — nothing was playing
                        }

                        animation.visible = true

                        try {
                            animation.start([ANIM.in], [false], 0, [1], () => {
                                if (isExiting) {
                                    return
                                }

                                animation.start([ANIM.loop], [true], 0, [1])
                                openBoxOnce()
                            })
                            requestAnimationFrame(() => {
                                animation.visible = true
                            })
                        } catch (error) {
                            console.warn('[UnfinishedRoundPopup] Failed to play box animation:', error)
                            continueOnce()
                            return
                        }

                        const inClipSeconds =
                            Number((animation as any).animation?.state?.tracks?.[0]?.animation?.duration) ||
                            BOX_OPEN_FALLBACK_DEFAULT_S

                        boxOpenFallback = gsap.delayedCall(inClipSeconds + BOX_OPEN_FALLBACK_MARGIN_S, () => {
                            boxOpenFallback = null

                            if (hasBoxOpened || isExiting) {
                                return
                            }

                            try {
                                animation.start([ANIM.loop], [true], 0, [1])
                            } catch (error) {
                                // ignore — the loop is cosmetic, the reveal below is not
                            }

                            openBoxOnce()
                        })
                    },
                },
                {
                    event: ComponentEvent.CUSTOM_NEW,
                    onGameSignalsTriggered: [(GameSignals as any).unfinishedRoundContinue],
                    callback: function (data: ICustomEventData) {
                        const animation: SpineAnimation = data.component.componentElement
                        const parentContainer = resolveParentContainer(data)

                        clearBoxOpenFallback()
                        detachTapHandler(parentContainer)

                        if (parentContainer) {
                            parentContainer.interactive = false
                            parentContainer.eventMode = 'auto'
                            parentContainer.cursor = 'default'
                        }

                        try {
                            animation.stop()
                        } catch (error) {
                            // ignore
                        }

                        animation.visible = false
                    },
                },
            ],
            scale: [
                { orientation: Orientation.LANDSCAPE, x: 0.7, y: 0.7 },
                { orientation: Orientation.PORTRAIT, x: 0.7, y: 0.7 },
            ],
            position: [
                { orientation: Orientation.LANDSCAPE, x: 960, y: 357 },
                { orientation: Orientation.PORTRAIT, x: 540, y: 880 },
            ],
        },
        createPopupTextItem({
            name: 'Unfinished Round Title',
            style: UNFINISHED_ROUND_TITLE_STYLE,
            position: [
                { orientation: Orientation.LANDSCAPE, x: 965, y: 520 },
                { orientation: Orientation.PORTRAIT, x: 540, y: 1040 },
            ],
            resolveText: () => translate('YOU HAVE UNFINISHED GAME', TITLE_FALLBACK),
        }),
        createPopupTextItem({
            name: 'Unfinished Round Continue',
            style: CONTINUE_STYLE,
            position: [
                { orientation: Orientation.LANDSCAPE, x: 965, y: 870 },
                { orientation: Orientation.PORTRAIT, x: 540, y: 1420 },
            ],
            resolveText: () => {
                const key = DeviceUtils.isMobileDevice() ? 'Tap to Continue' : 'Click to Continue'
                return translate(key, key)
            },
            pulse: true,
        }),
    ],
}
